import { useId, useState } from 'react';
import { Badge, Button } from 'react-daisyui';
import type { SettingRow } from '@/hooks/useSettings';
import { GenericModal } from '@/components/Modal';

interface ListDialogProps {
  row: SettingRow;
  entries: (number | string)[];
  environment: string | null;
  onConfirm: (value: (number | string)[], reason: string) => Promise<void>;
  onCancel: () => void;
}

// Edits a list as text, one entry per line. Saving replaces the whole list. Number lists turn each
// line into a number and refuse a line that is not a whole number. The first click on Save only
// shows what will be added and removed; a second click on "Yes, save" saves it.
export default function ListDialog({
  row,
  entries,
  environment,
  onConfirm,
  onCancel,
}: ListDialogProps) {
  const titleId = useId();
  const entriesId = useId();
  const reasonId = useId();
  const [text, setText] = useState(entries.map(String).join('\n'));
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState<{
    value: (number | string)[];
    added: string[];
    removed: string[];
    newOrder: string[] | null;
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const editText = (newText: string) => {
    setText(newText);
    setPending(null);
  };

  // First click: checks the lines and shows what changes. Second click: saves.
  const save = async () => {
    if (pending) {
      setIsSaving(true);
      try {
        await onConfirm(pending.value, reason);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    let value: (number | string)[] = lines;
    if (row.valueType === 'number_list') {
      const notANumber = lines.find((line) => !/^\d+$/.test(line));
      if (notANumber !== undefined) {
        setError(`"${notANumber}" is not a whole number.`);
        return;
      }
      value = lines.map(Number);
    }
    // createTeam adds the first entry of MICROSOFT_TEAM_OWNER to every new client Team, so an
    // empty list makes creating Teams fail. The api refuses it too.
    if (row.key === 'MICROSOFT_TEAM_OWNER' && value.length === 0) {
      setError('Keep at least one email here, or creating Teams fails.');
      return;
    }
    setError('');

    const before = entries.map(String);
    const after = value.map(String);
    const added = after.filter((entry) => !before.includes(entry));
    const removed = before.filter((entry) => !after.includes(entry));
    // Order counts on its own too: in MICROSOFT_TEAM_OWNER the first entry is the member added when
    // a Team is created, so moving an entry up is a real change.
    const orderChanged = after.join('\n') !== before.join('\n');
    if (!added.length && !removed.length && !orderChanged) {
      setError('No changes to save.');
      return;
    }
    setPending({
      value,
      added,
      removed,
      newOrder: !added.length && !removed.length ? after : null,
    });
  };

  return (
    <GenericModal isOpen onClose={onCancel}>
      <div role='dialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='flex items-center gap-2 pr-8 mb-4'>
          <h2 id={titleId} className='text-lg font-bold'>
            {row.label}
          </h2>
          {environment && (
            <Badge color='accent' variant='outline' size='sm'>
              {environment}
            </Badge>
          )}
        </div>
        <p className='text-sm'>
          <code className='font-mono'>{row.key}</code>: {row.description}
        </p>
        <label htmlFor={entriesId} className='form-control mt-4'>
          <span className='label label-text'>One entry per line</span>
          <textarea
            id={entriesId}
            className='textarea textarea-bordered font-mono'
            rows={6}
            value={text}
            onChange={(event) => editText(event.target.value)}
          />
        </label>
        {error && <p className='text-sm text-error mt-1'>{error}</p>}
        {pending && (
          <div className='text-sm text-warning mt-2'>
            <p>Are you sure? Click &quot;Yes, save&quot; to confirm.</p>
            {pending.added.length > 0 && (
              <p>Adds: {pending.added.join(', ')}</p>
            )}
            {pending.removed.length > 0 && (
              <p>Removes: {pending.removed.join(', ')}</p>
            )}
            {pending.newOrder && (
              <p>New order: {pending.newOrder.join(', ')}</p>
            )}
          </div>
        )}
        <label htmlFor={reasonId} className='form-control mt-4'>
          <span className='label label-text'>Reason (optional)</span>
          <textarea
            id={reasonId}
            className='textarea textarea-bordered'
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <div className='mt-6 flex justify-end gap-2'>
          <Button
            type='button'
            color='accent'
            variant='outline'
            size='sm'
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            color='accent'
            size='sm'
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={save}
            disabled={isSaving}
            loading={isSaving}
          >
            {pending ? 'Yes, save' : 'Save'}
          </Button>
        </div>
      </div>
    </GenericModal>
  );
}
